import React, { useCallback, useState } from "react";
import { AppBar, Toolbar, IconButton, Typography, Box, CircularProgress, Alert, Button } from "@mui/material";
import { Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import usePaginatedList from "../hooks/usePaginatedList";
import OrderCard from "../components/OrderCard";
import PaymentDialog from "../components/PaymentDialog";
import CancelReturnDialog from "../components/CancelReturnDialog";
import LoadMoreButton from "../components/LoadMoreButton";

export default function MyDeliveries() {
  const { store, logout } = useAuth();
  const navigate = useNavigate();
  const currency = store?.currency || store?.price_type || "";

  const [payingOrder, setPayingOrder] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");

  // GET pos/v1/orders/get-orders?st=delivery_active&page=N — this driver's
  // own claimed deliveries only (server-side delivery_by=Auth::id()).
  const fetchPage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders", { params: { st: "delivery_active", page } }).then((res) => {
        const paginator = res.data?.orders || {};
        return { items: paginator.data || [], hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    []
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore, reload } = usePaginatedList(fetchPage, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // PaymentDialog's action now does both payment and delivery in one step —
  // 'completed' is the same terminal transition POS uses to finish a sale,
  // and OrderController@updateOrderStatus accepts received/payment_method
  // alongside it in the same request. This used to be a separate
  // 'delivery_picked_up' call, requiring a second "Mark Delivered" tap.
  const handleConfirmPayment = async ({ payment_method, received }) => {
    await apiClient.post("orders/update-ord-status", {
      id: payingOrder.id,
      status: "completed",
      received,
      payment_method,
    });
    setPayingOrder(null);
    reload();
  };

  // Covers orders paid online before dispatch (payment_status already
  // 'done' when the driver gets it) — nothing left to collect, so this
  // skips PaymentDialog entirely and just closes the order out, keeping
  // whatever received_amount/payment_method were already recorded.
  const handleMarkDelivered = async (order) => {
    setBusyId(order.id);
    setActionError("");
    try {
      await apiClient.post("orders/update-ord-status", {
        id: order.id,
        status: "completed",
        received: order.received_amount,
      });
      reload();
    } catch (err) {
      setActionError(err.message || "Failed to mark as delivered");
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmReturn = async (reason) => {
    await apiClient.post(`orders/cancel/${cancellingOrder.id}`, { cancel_type: "return", cancel_reason: reason });
    setCancellingOrder(null);
    reload();
  };

  return (
    <Box>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            My Deliveries
          </Typography>
          <IconButton onClick={handleLogout} size="small">
            <Logout fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {(error || actionError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError || error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No active deliveries — pick up an order first.
          </Typography>
        ) : (
          <>
            {orders.map((o) => {
              const isPaid = o.payment_status === "done";
              return (
                <OrderCard
                  key={o.id}
                  order={o}
                  currency={currency}
                  actions={
                    <>
                      {isPaid ? (
                        // Already paid (e.g. online, before dispatch) — nothing
                        // left to collect, so this skips straight to closing
                        // the order out instead of reopening PaymentDialog.
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={busyId === o.id}
                          onClick={() => handleMarkDelivered(o)}
                        >
                          {busyId === o.id ? "Saving..." : "Mark as Delivered"}
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => setPayingOrder(o)}>
                          Collect Payment
                        </Button>
                      )}
                      <Button size="small" variant="outlined" color="error" onClick={() => setCancellingOrder(o)}>
                        Cancel as Return
                      </Button>
                    </>
                  }
                />
              );
            })}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>

      <PaymentDialog order={payingOrder} currency={currency} onClose={() => setPayingOrder(null)} onConfirm={handleConfirmPayment} />
      <CancelReturnDialog order={cancellingOrder} onClose={() => setCancellingOrder(null)} onConfirm={handleConfirmReturn} />
    </Box>
  );
}
